import * as React from 'react'

import { type NonFsdsTheme,resolveNonFsdsTheme } from '@/lib/non-fsds-theme'

interface NonFsdsMoneyBackGuaranteeSectionProps {
  title: string
  description: string
  theme?: NonFsdsTheme | string
}

export default function NonFsdsMoneyBackGuaranteeSection({
  title,
  description,
  theme = 'general',
}: NonFsdsMoneyBackGuaranteeSectionProps) {
  const themeStyles = {
    general: {
      bgGradient: 'from-blue-600 via-blue-700 to-sky-700',
      blob1: 'bg-blue-500/30',
      blob2: 'bg-sky-400/20',
      textColor: 'text-blue-50/90',
    },
    'english-academy': {
      bgGradient: 'from-blue-600 via-blue-700 to-emerald-600',
      blob1: 'bg-blue-500/30',
      blob2: 'bg-emerald-400/20',
      textColor: 'text-blue-50/90',
    },
    programming: {
      bgGradient: 'from-indigo-600 via-indigo-700 to-cyan-700',
      blob1: 'bg-indigo-500/30',
      blob2: 'bg-cyan-400/20',
      textColor: 'text-indigo-50/90',
    },
    math: {
      bgGradient: 'from-teal-600 via-teal-700 to-blue-700',
      blob1: 'bg-teal-500/30',
      blob2: 'bg-blue-400/20',
      textColor: 'text-teal-50/90',
    },
  }[resolveNonFsdsTheme(theme)]

  return (
    <section data-testid="non-fsds-money-back-guarantee-section" className="px-4 py-12 sm:px-6 sm:py-16">
      <div className={`relative mx-auto max-w-5xl overflow-hidden rounded-[2.5rem] bg-gradient-to-br px-6 py-14 shadow-xl sm:px-12 sm:py-20 md:px-16 text-center ${themeStyles.bgGradient}`}>
        {/* Decorative blobs */}
        <div className={`pointer-events-none absolute -left-20 -top-20 size-64 rounded-full blur-3xl ${themeStyles.blob1}`} />
        <div className={`pointer-events-none absolute -bottom-32 -right-32 size-96 rounded-full blur-3xl ${themeStyles.blob2}`} />

        <div className="relative z-10 flex flex-col items-center">
          <div className="mb-6 flex size-16 mx-auto items-center justify-center rounded-2xl bg-white/10 text-white backdrop-blur-sm ring-1 ring-white/20">
            <svg
              className="size-8"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={2}
              stroke="currentColor"
              aria-hidden="true"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M9 12.75 11.25 15 15 9.75M21 12c0 1.268-.63 2.39-1.593 3.068a3.745 3.745 0 0 1-1.043 3.296 3.745 3.745 0 0 1-3.296 1.043A3.745 3.745 0 0 1 12 21c-1.268 0-2.39-.63-3.068-1.593a3.746 3.746 0 0 1-3.296-1.043 3.745 3.745 0 0 1-1.043-3.296A3.745 3.745 0 0 1 3 12c0-1.268.63-2.39 1.593-3.068a3.745 3.745 0 0 1 1.043-3.296 3.746 3.746 0 0 1 3.296-1.043A3.746 3.746 0 0 1 12 3c1.268 0 2.39.63 3.068 1.593a3.746 3.746 0 0 1 3.296 1.043 3.746 3.746 0 0 1 1.043 3.296A3.745 3.745 0 0 1 21 12Z"
              />
            </svg>
          </div>
          <h2 className="text-3xl font-bold text-white tracking-tight sm:text-4xl md:text-5xl">{title}</h2>
          <p className={`mt-5 max-w-2xl text-base leading-relaxed sm:text-lg ${themeStyles.textColor}`}>{description}</p>
        </div>
      </div>
    </section>
  )
}
